import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { OrderLine, SalesOrderDetails, ProductSearch, TradeTerm, StatusWithFlags, FinanceStatus, Dispatch, DispatchDeadline, Duplicate } from '@equipmyschool/emsadminweb-models';
import { MatTableDataSource } from '@angular/material/table';
import { BehaviorSubject, Observable, Subject, combineLatest, firstValueFrom } from 'rxjs';
import { map, shareReplay, tap, takeUntil } from 'rxjs/operators';
import { OrderProcessingService, StatusService, SupplierPreferenceService, FinanceStatusService, DispatchService, ProductService, OrderLineService, NoteService, ElasticSearchService, AccountService } from 'app/server/server.module';
import { ConfirmDialogComponent, ConfirmationType, DialogResponse } from 'app/shared/confirm-dialog/confirm-dialog.component';
import { NotesDialogComponent } from '../coad-web/dispatch-details/pages/dialogs/notes-dialog/notes-dialog.component';
import { DataAccessWrapperDialogComponent } from 'app/data-access/data-access-wrapper-dialog.component';
import { SelectionDialogComponent } from 'app/shared/selection-dialog/selection-dialog.component';
import { DuplicationDialogComponent } from './duplication-dialog/duplication-dialog.component';

interface ProductSelectCriteria {
  fromLine: boolean;
  productId: number;
  emsProductId: number;
}

@Component({
  selector: 'app-order-processing',
  templateUrl: './order-processing.component.html',
  styleUrls: ['./order-processing.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderProcessingComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private _productSelectionCriteria$ = new BehaviorSubject<ProductSelectCriteria | null>(null);
  private invoiceLineValue = 0;  
  private stack: number[] = [];
  
  public line: OrderLine = {} as OrderLine;
  public selectedProduct?: ProductSearch | null;
  public initLine?: OrderLine;
  public initProduct?: ProductSearch | null;
  public current = 0;
  public canChangeProduct = false;
  public displayedColumns: string[] = ['Notes', 'Supplier', 'Code', 'Description', 'Catalogue', 'Unit', 'Cost', 'Price', 'Commodity', 'Stock', 'DataSource', 'Status', 'LastImported', 'PricingExpiry', 'Distributor'];
  public initialUnitPrice = 0;

  public readonly line$: Observable<OrderLine | null>;
  public readonly order$: Observable<SalesOrderDetails | null>;
  public readonly status$: Observable<StatusWithFlags | undefined>;
  public readonly financeStatus$: Observable<FinanceStatus>;
  public readonly dispatch$: Observable<Dispatch>;
  public readonly dispatchDeadlines$: Observable<DispatchDeadline[]>;
  public readonly dataSource$: Observable<MatTableDataSource<ProductSearch>>;
  public readonly tradeTerms$: Observable<TradeTerm[]>;
  public readonly selectedProduct$: Observable<ProductSearch | null>;
  public readonly productSelectionCriteria$ = this._productSelectionCriteria$.asObservable();
  public readonly discounts$: Observable<TradeTerm[]>;
  public readonly lineDiscount$: Observable<number>;
  public readonly lineSurcharge$: Observable<number>;
  public readonly invoiceDiscount$: Observable<number>;
  public readonly invoiceSurcharge$: Observable<number>;

  private dialogRefDataAccess?: MatDialogRef<DataAccessWrapperDialogComponent>;
  public currentDiscount: number = 0;

  constructor(
    private orderProcessingService: OrderProcessingService,
    private statusService: StatusService,
    private supplierPreferenceService: SupplierPreferenceService,
    private financeStatusService: FinanceStatusService,
    private dispatchService: DispatchService,
    private productService: ProductService,
    private orderLineService: OrderLineService,
    private elasticSearchService: ElasticSearchService,
    private accountService: AccountService,
    private noteService: NoteService,
    private dialog: MatDialog,
    private dialogRef?: MatDialogRef<OrderProcessingComponent>,
    @Inject(MAT_DIALOG_DATA) private data?: number[]
  ) {
    this.stack = data ?? [];
    this.current = 0;
    this.order$ = this.orderProcessingService.orderDetails$;

    this.status$ = this.statusService.status$.pipe(
      tap(status => {
        if (status) {
          this.canChangeProduct = !(status.Dispatched || status.Ordered && !status.NotAvailable);
        }
      }),
      shareReplay(1)
    );
    
    this.financeStatus$ = this.financeStatusService.financeStatus$;
    this.dispatch$ = this.dispatchService.dispatch$;
    this.dispatchDeadlines$ = this.dispatchService.deadlines$;

    this.line$ = this.orderProcessingService.orderLine$.pipe(
      tap(line => {
        if (line) {
          this.line = line;
          this.initLine = {...line};
          this.invoiceLineValue = line?.LineValue ?? 0;
          this.initialUnitPrice = line?.EstimatedUnitPrice ?? 0;
          this.statusService.getStatus(line?.Status ?? 0);
          this.financeStatusService.getFinanceStatus(line?.FinanceStatus ?? 0);
          this.currentDiscount = line?.CustomerDiscount ?? 0;
          this._productSelectionCriteria$.next(this.createProductSelectCriteria(line));
        }
      }),
      shareReplay(1)
    );

    this.tradeTerms$ = this.order$.pipe(map(order => order?.TradeTerms ?? []));
    this.dataSource$ = this.orderProcessingService.productSearchList$.pipe(map(products => new MatTableDataSource(products)));

    this.selectedProduct$ = combineLatest([this.orderProcessingService.productSearchList$, this.productSelectionCriteria$]).pipe(
      map(([products, criteria]) => this.findSelectedProduct(products, criteria)),
      tap(product => {
        if (product) {
          this.selectedProduct = product;
          this.supplierPreferenceService.getSupplierPreference(product.SupplierStatus);
          if (this.initLine?.Product !== null && this.initLine?.Product === product.EMSProductID) {
            this.initProduct = {...product};
          }
        }
      }),
      shareReplay(1)
    );

    this.discounts$ = combineLatest([this.tradeTerms$, this.selectedProduct$]).pipe(
      map(([tradeTerms, product]) => this.filterTradeTerms(tradeTerms, product)),
      shareReplay(1)
    );

    this.lineDiscount$ = this.discounts$.pipe(
      map(discounts => 1 - this.applyDiscounts(discounts, discount => !discount.InvoiceLevel && !discount.Surcharge, false)),
      shareReplay(1)
    );

    this.lineSurcharge$ = this.discounts$.pipe(
      map(discounts => this.applyDiscounts(discounts, discount => !discount.InvoiceLevel && discount.Surcharge, true) - 1),
      shareReplay(1)
    );

    this.invoiceDiscount$ = this.discounts$.pipe(
      map(discounts => 1 - this.applyDiscounts(discounts, discount => discount.InvoiceLevel && !discount.Surcharge, false)),
      shareReplay(1)
    );

    this.invoiceSurcharge$ = this.discounts$.pipe(
      map(discounts => this.applyDiscounts(discounts, discount => discount.InvoiceLevel && discount.Surcharge, true) - 1),
      shareReplay(1)
    );
  }

  public ngOnInit(): void {
    this.line$.pipe(takeUntil(this.destroy$)).subscribe();
    const ids = this.data;
    if (ids && ids.length) {
      this.getLine(ids[0]);
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public async searchByDescription(): Promise<void> {
    const selectedLine = await firstValueFrom(this.line$);
    const order = await firstValueFrom(this.order$);
    const token: string = await firstValueFrom(this.accountService.getClientToken(order?.Client.CompanyID ?? 0));

    if ((selectedLine?.Description ?? "").trim().length > 0) {
      this.elasticSearchService.search((selectedLine?.Description ?? "").trim(), token).subscribe(results => {
        // Merge SearchResultsWithFacets into datasource
        if (results) {
          results.Results.forEach(product => {
            this.elasticSearchService.getProduct(product.CommonDetailID, token).subscribe(productDetails => {
              this.orderProcessingService.mergeProducts(product, productDetails.Details.Products);
            });
          });
        }
      });
    }
  }

  private createProductSelectCriteria(line: OrderLine | null): ProductSelectCriteria | null {
    if (!line) {
      return null;
    }

    return {
      fromLine: true,
      productId: 0,
      emsProductId: line.Product ?? 0
    };
  }

  public startSearch() {
    this.orderProcessingService.searchProducts(this.line?.Product ? [this.line?.Product] : [], this.line?.Reference ? [this.line?.Reference] : [], 1.0);
  }

  private findSelectedProduct(products: ProductSearch[], criteria: ProductSelectCriteria | null): ProductSearch | null {
    if (!criteria) {
      return null;
    }

    return products.find(product =>
      (criteria.fromLine || product.ProductID === criteria.productId) && product.EMSProductID === criteria.emsProductId
    ) ?? null;
  }

  private filterTradeTerms(tradeTerms: TradeTerm[], product: ProductSearch | null): TradeTerm[] {
    return tradeTerms.filter(tradeTerm =>
      !tradeTerm.OnlyWhenInCredit && (!tradeTerm.AppliesTo || tradeTerm.AppliesTo === product?.CommodityType)
    );
  }

  private applyDiscounts(discounts: TradeTerm[], predicate: (term: TradeTerm) => boolean, surcharge: boolean): number {
    return discounts
      .filter(predicate)
      .reduce((acc, discount) => acc * (surcharge ? 1 + discount.Value / 100 : 1 - discount.Value / 100), 1);
  }

  private getLine(lineId: number): void {
    this.orderProcessingService.getLine(lineId);
  }

  public selectProduct(product: ProductSearch): void {
    this._productSelectionCriteria$.next({
      fromLine: false,
      productId: product.ProductID,
      emsProductId: product.EMSProductID
    });
  }

  public newProduct(): void {
    this.dialogRefDataAccess = this.dialog.open(DataAccessWrapperDialogComponent, {
      data: 0,
      width: '1000px',
      panelClass: 'data-access-dialog'
    });

    this.dialogRefDataAccess.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: any) => {
      if (result) {
        this.orderProcessingService.searchProducts([result.ProductID, this.line?.Product], [result.Code, this.line?.Reference], 1.0);
      }
    });
  }

  public async editProduct(): Promise<void> {
    const selectedProduct = await firstValueFrom(this.selectedProduct$);
    if (!selectedProduct) {
      return;
    }

    this.dialogRefDataAccess = this.dialog.open(DataAccessWrapperDialogComponent, {
      data: selectedProduct.EMSProductID,
      width: '1000px',
      panelClass: 'data-access-dialog'
    });

    this.dialogRefDataAccess.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: any) => {
      if (result) {
        this.startSearch();
      }
    });
  }

  public async accept(): Promise<void> {
    if (!await this.validateLine(true)) {
      return;
    }

    if (!this.line?.Reference) {
      alert('Please specify a product code for this item');
      return;
    }

    const [currOrder, currStatus, currProduct, currDispatch] = await firstValueFrom(
      combineLatest([this.order$, this.status$, this.selectedProduct$, this.dispatch$])
    );

    const selectedLine = this.line;
    if (!selectedLine || !currOrder || !currStatus) return;
    selectedLine.SalesOrder = currOrder.SOID;

    if (this.canChangeProduct) {
      if (!selectedLine.LineID || currStatus.Cancelled) {
        selectedLine.Status = 20;
      } else if (
        selectedLine.Status === 0 ||
        selectedLine.Status === 2011 ||
        selectedLine.Status === 2017 ||
        selectedLine.Status === 2046 ||
        selectedLine.Status === 2047 ||
        selectedLine.Status === 2048
      ) {
        // keep the current status
      } else if (!currStatus.Confirmed) {
        selectedLine.Status = 20;
      } else {
        selectedLine.Status = 45;
      }

      if (currProduct?.NotAvailable && (!selectedLine.LineID || !currStatus.NotAvailable)) {
        if (!confirm('Are you sure you want to save this as not available? If you continue the product will be reported to the customer and not ordered')) {
          return;
        }

        if (currStatus.Confirmed) {
          selectedLine.Status = currProduct.StatusID;
        }

        selectedLine.UnitPrice = 0;
        selectedLine.CustomerDiscount = 0;
        selectedLine.HandlingCharge = 0;
      }

      if (this.initLine && selectedLine.LineID && (this.invoiceLineValue !== this.initLine.LineValue || selectedLine.HandlingCharge !== this.initLine.HandlingCharge)) {
        const pfiLineDetails = this.orderProcessingService.getOutstandingPfiCount(selectedLine.LineID);
        if (pfiLineDetails.Printed > 0) {
          if (!confirm(`Accepting the line at this value will invalidate ${pfiLineDetails.Printed} ProForma Invoice(s). Do you wish to continue?`)) {
            return;
          }
        }

        if (pfiLineDetails.Printed + pfiLineDetails.Checked > 0) {
          this.orderProcessingService.cancelOutstandingPfis(selectedLine.LineID, true);
        }
      }

      if (await this.checkForDuplicates()) {
        return;
      }

      if (this.initLine && selectedLine.LineID) {
        if (this.initLine.Status === 46 && this.initLine.Product === currProduct?.EMSProductID) {
          const response = await this.getConfirmation(
            'This product was Market Restricted. Do you want to re-order it for a different country? Clicking No will report it to the customer as Market Restricted!',
            ConfirmationType.YesNoCancel
          );

          if (response === DialogResponse.Yes) {
            selectedLine.Status = 49;
          } else if (response === DialogResponse.No) {
            selectedLine.Status = 104;
          } else {
            return;
          }
        }

        if (currDispatch && currProduct && currDispatch.LeavingFrom !== currProduct.DefaultShipTo) {
          if ((await this.getConfirmation('The product you have selected will need to be sent in an IWD. Are you sure you wish to continue?', ConfirmationType.YesNo)) !== DialogResponse.Yes) {
            return;
          }
        }

        if (currStatus.Cancelled) {
          selectedLine.LineID = await this.orderProcessingService.addLine(selectedLine, currProduct);
          this.stack[this.current] = selectedLine.LineID;
        } else {
          this.orderProcessingService.editLine(selectedLine, currProduct);
          if (this.initLine.Product) {
            this.orderProcessingService.checkForCountryChange(this.initLine.Product, currProduct);
          }

          if (currStatus.Confirmed) {
            this.dispatchService.checkRemainingLines(selectedLine.LineID);
          }
        }
      } else {
        selectedLine.LineID = await this.orderProcessingService.addLine(selectedLine, currProduct);
        this.stack.push(selectedLine.LineID);
      }
    } else if (this.initLine && selectedLine.LineID) {
      if (this.initLine.FinanceStatus === 70 || this.initLine.FinanceStatus === 79) {
        selectedLine.FinanceStatus = 79;
      } else if (this.initLine.FinanceStatus === 8070) {
        selectedLine.FinanceStatus = 80;
      }
      this.orderProcessingService.editLine(selectedLine, currProduct);
    }

    this.line = selectedLine;
    this.resetForm();

    if (this.stack && this.current < this.stack.length - 1) {
      this.current += 1;
      this.orderProcessingService.getLine(this.stack[this.current]);
    }
    else {
      this.current += 1;
      this.orderProcessingService.getLine(0);
    }
  }

  private resetForm(): void {
    this._productSelectionCriteria$.next(null);
    this.line = {} as OrderLine;
    this.initLine = undefined;
    this.initProduct = null;
    this.selectedProduct = null;
    this.canChangeProduct = false;
    this.invoiceLineValue = 0;
    this.initialUnitPrice = 0;
  }

  public async getNextLine(): Promise<void> {
    const selectedLine = await firstValueFrom(this.line$);
    const order = await firstValueFrom(this.order$);
    if (!selectedLine || !order) return;

    if (!await this.validateLine(false)) {
      return;
    }

    if (selectedLine.LineID) {
      this.orderProcessingService.editLine(selectedLine, null);
    }

    else {
      selectedLine.Status = 14;
      if (selectedLine.SalesOrder === undefined) selectedLine.SalesOrder = order.SOID;
      const lineId = await this.orderProcessingService.addLine(selectedLine, null);
      if (lineId) {
        this.stack.push(lineId);
      }
    }

    this.resetForm();

    if (this.stack && this.current < this.stack.length - 1) {
      this.current += 1;
      this.orderProcessingService.getLine(this.stack[this.current]);
    }
    else {
      this.current += 1;
      this.orderProcessingService.getLine(0);
    }
  }

  public async getPreviousLine(): Promise<void> {
    const selectedLine = await firstValueFrom(this.line$);
    const order = await firstValueFrom(this.order$);
    if (!selectedLine || !order) return;
  
    if (selectedLine.LineID) {
      if (!await this.validateLine(false)) {
        return;
      }

      this.orderProcessingService.editLine(selectedLine, null);
    }

    else if (selectedLine.Description.trim().length > 0) {
      var resp = await this.getConfirmation('You are currently in the process of typing a new line. Would you like to save this line?', ConfirmationType.YesNoCancel);
      if (resp === DialogResponse.Yes) {
        if (!await this.validateLine(false)) {
          return;
        }
        
        selectedLine.Status = 14;
        if (selectedLine.SalesOrder === undefined) selectedLine.SalesOrder = order.SOID;
        const lineId = await this.orderProcessingService.addLine(selectedLine, null);
        if (lineId) {
          this.stack.push(lineId);
        }
      }
      else if (resp === DialogResponse.Cancel) {
        return;
      }
    }

    if (this.current > 0) {
      this.resetForm();
      this.current -= 1;
      this.orderProcessingService.getLine(this.stack[this.current]);
    }
  }

  private async validateLine(productRequired: boolean): Promise<boolean> {
    var [currProduct, currSupplierPreference, currStatus] = await firstValueFrom(
      combineLatest([this.selectedProduct$, this.supplierPreferenceService.supplierPreference$, this.status$])
    );

    var errors: string[] = [];

    if (!this.line?.Description) {
      errors.push('Please enter a description for the product');
    }

    if (productRequired && !currProduct) {
      errors.push('Please select a product before clicking accept');
    }

    if (errors.length > 0) {
      alert(errors.join('\n'));
      return false;
    }

    if (this.line) {
      this.line.Reference = (this.line.Reference ?? '').trim();
      this.line.Description = (this.line.Description ?? '').trim();
    }

    if (productRequired && currProduct) {
      if (currProduct.SupplierStatus === 9999 || currSupplierPreference.Block) {
        alert('Please select a product from a valid supplier');
        return false;
      }

      if (currSupplierPreference.Warn) {
        if (!confirm(`${currProduct.Supplier} is marked as ${currSupplierPreference.Name}. Are you sure you want to continue?`)) {
          return false;
        }
      } else if (!this.pricingValid(currProduct.PricingExpiry)) {
        if (!confirm('The costs for the selected product have expired. Are you sure you want to continue?')) {
          return false;
        }
      }
    }

    if (currStatus?.Confirmed && this.line && this.initLine) {
      if (
        this.initLine.LineValue !== this.line.LineValue ||
        this.initLine.UnitPrice !== this.line.UnitPrice ||
        this.initLine.Quantity !== this.line.Quantity
      ) {
        if (!confirm('The line has been modified since it was confirmed. Are you sure you want to accept the changes?')) {
          return false;
        }
      }
    }

    return true;
  }

  public async checkForDuplicates(): Promise<boolean> {
    const selectedLine = await firstValueFrom(this.line$);
    const order = await firstValueFrom(this.order$);
    const selectedProduct = await firstValueFrom(this.selectedProduct$);
    if (!selectedLine || !order) return false;

    const duplicates = await firstValueFrom(this.orderLineService.getDuplications(
      order.Client.CompanyID ?? 0,
      selectedProduct?.ProductID ?? 0,
      selectedProduct?.Code ?? "",
      order.SOID,
      selectedLine.POLine,
      selectedLine.LineID ?? 0
    ));

    if (duplicates.length > 0) {
      await this.showDuplications(duplicates);
      return !confirm('Duplication Detected! Are you sure you wish to Accept?');
    }

    return false;
  }

  public async showDuplications(duplicates: Duplicate[]): Promise<void> {
    const selectedLine = await firstValueFrom(this.line$);
    const order = await firstValueFrom(this.order$);

    var dupConfirmRef = this.dialog.open(DuplicationDialogComponent, {
      id: 'duplicate-dialog',
      width: '560px',
      disableClose: false,
      panelClass: 'confirmation-pane',
      data: {
        line: selectedLine,
        order: order,
        duplicates: duplicates
      }
    });

    await firstValueFrom(dupConfirmRef.afterClosed());
  }

  public getConfirmation(message: string, type: ConfirmationType): Promise<DialogResponse> {
    return firstValueFrom(
      this.dialog
        .open(ConfirmDialogComponent, {
          data: {
            text: message,
            type: type
          }
        })
        .afterClosed()
    );
  }
  
  public viewNotes(table: string, id: number): void {
    this.dialog.open(NotesDialogComponent, {
      id: 'notes-dialog',
      width: '560px',
      disableClose: false,
      panelClass: 'confirmation-pane',
      data: {
        tableName: table,
        id: id
      }
    });
  }

  public minCost(): void {
    if (this.line) {
      this.line.UnitPrice = this.line.EstimatedUnitPrice;
    }
  }

  public maxCost(): void {
    if (this.line) {
      if (this.line.Quantity == 0) {
        this.line.UnitPrice = 0;
        return;
      }

      if (this.selectedProduct$ && this.order$) {
        combineLatest([this.selectedProduct$, this.order$]).pipe(tap(([product, order]) => {
          if (this.line && product && order) {
            if (product.UnitValue == 0) {
              this.line.UnitPrice = product.UnitCost * product.Exchange / order.Currency.ExchangeRate;
            }
            else {
              this.line.UnitPrice = product.UnitValue * product.Exchange / order.Currency.ExchangeRate;
            }
          }
        })).subscribe();
      }

      this.line.UnitPrice = (this.line.UnitPrice ?? 0).toFixed(2) as unknown as number;
    }
  }

  public normaliseCost(): void {
    if (this.line) {
      this.line.UnitPrice = this.normaliseValue(this.line.UnitPrice ?? 0);
    }
  }

  public normaliseValue(value: number): number {
    var strVal = value.toFixed(2);
    var retValue = strVal as unknown as number;

    switch (strVal.substring(strVal.length - 1, strVal.length)) {
      case "1":
      case "2":
      case "3":
      case "4":
        retValue = strVal.substring(0, strVal.length - 1) + "5" as unknown as number;
        break;
      case "6":
      case "7":
      case "8":
        retValue = strVal.substring(0, strVal.length - 1) + "9" as unknown as number;
        break;
    }

    return retValue;
  }

  public disc0(): void {
    if (this.line) {
      this.line.CustomerDiscount = 0;
    }
  }

  public disc7_5(): void {
    if (this.line) {
      this.line.CustomerDiscount = 7.5;
    }
  }

  public disc15(): void {
    if (this.line) {
      this.line.CustomerDiscount = 15;
    }
  }

  public discCurrent(): void {
    if (this.line) {
      this.line.CustomerDiscount = this.currentDiscount;
    }
  }

  public discMax(): void {
    if (this.line) {
      combineLatest([this.discounts$, this.selectedProduct$]).pipe(
        tap(([discounts, product]) => {
          if (this.line && product) this.line.CustomerDiscount = ((1 - this.applyDiscounts(discounts, discount => !discount.OnlyWhenInCredit && discount.MinSupplierDiscount <= (product?.Discount * 100) && (discount.AppliesTo === '' || discount.AppliesTo === product?.CommodityType) && !discount.InvoiceLevel && !discount.Surcharge, false)) * 100).toFixed(1) as unknown as number;
        })
      ).subscribe();
    }
  }

  public priceCat(): void {
    if (this.line) {
      combineLatest([this.order$, this.selectedProduct$]).pipe(
        tap(([order, product]) => {
        if (this.line && product && order) this.line.UnitPrice = (product.UnitCost * ((this.line.ProductQuantity ?? 0) / this.line.Quantity) * (product.CurrencyCode === order.Currency.Code ? 1 : product.Exchange / order.Currency.ExchangeRate)).toFixed(2) as unknown as number;
        })
      ).subscribe();
    }
  }

  public priceCatPlus10(): void {
    if (this.line) {
      combineLatest([this.order$, this.selectedProduct$]).pipe(
        tap(([order, product]) => {
          if (this.line && product && order) this.line.UnitPrice = this.normaliseValue(product.UnitCost * ((this.line.ProductQuantity ?? 0) / this.line.Quantity) * (product.CurrencyCode === order.Currency.Code ? 1 : product.Exchange / order.Currency.ExchangeRate) / 0.9);
        })
      ).subscribe();
    }
  }

  public priceProductSalePrice(): void {
    if (this.line) {
      combineLatest([this.order$, this.selectedProduct$]).pipe(
        tap(([order, product]) => {
          if (this.line && product && order) this.line.UnitPrice = this.initialUnitPrice > 0 && order.OrderType === 4 ? this.initialUnitPrice : this.roundNumber(product.UnitValue * (product.CurrencyCode === order.Currency.Code ? 1 : product.Exchange / order.Currency.ExchangeRate), 2);
        })
      ).subscribe();
    }
  }

  public priceDesiredProfit(): void {
    this.setProfitMargin(0.25);
  }

  public price15PercentProfit(): void {
    this.setProfitMargin(0.15);
  }

  public priceMinProfit(): void {
    this.setProfitMargin(0.1);
  }

  private setProfitMargin(margin: number): void {
    if (this.line) {
      combineLatest([this.order$, this.selectedProduct$, this.invoiceDiscount$, this.invoiceSurcharge$, this.lineSurcharge$, this.financeStatus$]).pipe(
        tap(([order, product, invoiceDiscount, invoiceSurcharge, lineSurcharge, financeStatus]) => {
          if (this.line && product && order) {
            var suppDiscount = product.Discount;
            while (suppDiscount > 1) {
              suppDiscount /= 100;
            }

            const LineCost = financeStatus.Priced ? this.line.LineCost ?? 0 : (this.line.ProductQuantity ?? 0) * ((product.UnitCost * (1 - suppDiscount) * product.Exchange).toFixed(2) as unknown as number);
            var price = (LineCost / order.Currency.ExchangeRate) / (1 - margin);
            price /= (1 - invoiceDiscount);
            price *= (1 - invoiceSurcharge);
            price /= (this.line.Quantity ?? 1);
            price /= (100 - (this.line.CustomerDiscount ?? 0)) / 100;
            price *= (1 - lineSurcharge);

            this.line.UnitPrice = this.normaliseValue(price);
          }
        })
      ).subscribe();
    }
  }

  public roundNumber(value: number, decimals: number): number {
    const factor = Math.pow(10, decimals);
    return (Math.round(value * factor) / factor).toFixed(decimals) as unknown as number;
  }

  closeDialog(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  public async directSupply(): Promise<void> {
    const selectedLine = await firstValueFrom(this.line$);
    const order = await firstValueFrom(this.order$);
    if (!selectedLine || !order) return;

    var selectRef = this.dialog.open(SelectionDialogComponent, {
      data: {
        title: 'Please select the reason for the Direct Supply lines.',
        options: [
          { data: "", name: "None" },
          { data: "LIVE", name: "Live" },
          { data: "HAZARDOUS", name: "Hazardous" },
          { data: "AUS", name: "Australian" },
          { data: "VIRTUAL", name: "Virtual" },
          { data: "DIRECT", name: "Direct" }
        ],
      }
    });
    
    let strNewSO = "";
    selectRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(async (result: any | undefined) => {
      if (result) {
        strNewSO = result;
      }
    });

    if (strNewSO.length > 0) strNewSO = strNewSO + " / "

    if (order.AllocationName.toUpperCase().startsWith("LIVE / ")) {
      strNewSO = strNewSO + order.AllocationName.substring(7);
    }
    else if (order.AllocationName.toUpperCase().startsWith("HAZARDOUS / ")) {
      strNewSO = strNewSO + order.AllocationName.substring(12);
    }
    else if (order.AllocationName.toUpperCase().startsWith("AUS / ")) {
      strNewSO = strNewSO + order.AllocationName.substring(6);
    }
    else if (order.AllocationName.toUpperCase().startsWith("VIRTUAL / ")) {
      strNewSO = strNewSO + order.AllocationName.substring(10);
    }
    else if (order.AllocationName.toUpperCase().startsWith("DIRECT / ")) {
      strNewSO = strNewSO + order.AllocationName.substring(9);
    }
    else {
      strNewSO = strNewSO + order.AllocationName;
    }

    if (strNewSO !== order.AllocationName) {
      this.orderProcessingService.changeSalesOrder(selectedLine.LineID ?? 0, strNewSO);
    }
  }

  public async pricingConfirmedOk(): Promise<void> {
    const selectedProduct = await firstValueFrom(this.selectedProduct$);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Get today's date without time
    if (selectedProduct && !this.pricingValid(selectedProduct.PricingExpiry)) {
      const expiryDate = new Date(today.getFullYear() + (today.getMonth() > 8 ? 1 : 0), today.getMonth() > 8 ? today.getMonth() - 9 : today.getMonth() + 3, today.getDate());
      this.productService.updatePricingExpiry(selectedProduct.EMSProductID, expiryDate).subscribe(() => {
        selectedProduct.PricingExpiry = expiryDate;
        this._productSelectionCriteria$.next(this._productSelectionCriteria$.getValue());
      });
    }
  }

  public pricingValid(expiry: any): boolean {
    if (!expiry) return false;
    const d = expiry instanceof Date ? expiry : new Date(expiry);
    if (isNaN(d.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    d.setHours(0, 0, 0, 0);
    return d >= today;
  }

  public async notAvailable(): Promise<void> {
    const selectedLine = await firstValueFrom(this.line$);
    const order = await firstValueFrom(this.order$);
    if (!selectedLine || !order) return;

    var lineId: number = selectedLine.LineID ?? 0;
    const status = await firstValueFrom(this.status$);
    const ordered = (selectedLine?.LineID ?? 0) > 0 && (status?.Confirmed ?? false);
    const notAvailableStatuses = this.statusService.statuses$.pipe(
      map(statuses => statuses.filter(status => status.ProductRelated && status.NotAvailable && !status.Cancelled && (!ordered && status.StatusID != 110 || status.Ordered === ordered)).forEach(status => ({ data: status, name: status.Description })))
    ).subscribe();

    var selectRef = this.dialog.open(SelectionDialogComponent, {
      data: {
        title: 'Select Not Available Status',
        options: notAvailableStatuses,
      }
    });

    var statusId: number = 0;
    selectRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(async (result: StatusWithFlags | undefined) => {
      if (result) {
        statusId = result.StatusID;
      }
    });

    if (!await this.validateLine(false) || statusId === 0) {
      return;
    }

    else if (selectedLine.LineID) {
      this.orderProcessingService.editLine(selectedLine, null);
    }

    else {
      selectedLine.Status = 13;
      if (selectedLine.SalesOrder === undefined) selectedLine.SalesOrder = order.SOID;
      lineId = await this.orderProcessingService.addLine(selectedLine, null);
      if (lineId) {
        this.stack[this.current] = lineId;
      }
    }

    this.orderLineService.createNotAvailableProducts([lineId], statusId);

    this.resetForm();

    if (this.stack && this.current < this.stack.length - 1) {
      this.current += 1;
      this.orderProcessingService.getLine(this.stack[this.current]);
    }
    else {
      this.current += 1;
      this.orderProcessingService.getLine(0);
    }
  }

  public async toQueryWithClient(): Promise<void> {
    if (!await this.validateLine(false)) {
      return;
    }

    const selectedLine = await firstValueFrom(this.line$);
    const status = await firstValueFrom(this.status$);
    if (!selectedLine || !status) return;

    const initialProduct = this.initProduct;
    selectedLine.Status = 1017;

    if (selectedLine.LineID === 0 || selectedLine.LineID === undefined) {
      const lineId = await this.orderProcessingService.addLine(selectedLine, null);
      this.noteService.add("OrderLines", lineId, "Line moved to Reviewed with Client.");
      if (lineId) {
        this.stack[this.current] = lineId;
      }
    }
    else if (
      selectedLine.Status === 0 ||
      selectedLine.Status === 2011 ||
      selectedLine.Status === 2017 ||
      selectedLine.Status === 2046 ||
      selectedLine.Status === 2047 ||
      selectedLine.Status === 2048
    ) {
      this.orderLineService.updateStatus(selectedLine.LineID, 2017);
      this.noteService.add("OrderLines", selectedLine.LineID, "Unpriced line marked as needing review by Client.");
    }
    else if (
      selectedLine.Status === 46 ||
      selectedLine.Status === 47
    ) {
      this.orderLineService.updateStatus(selectedLine.LineID, 1046);
      this.noteService.add("OrderLines", selectedLine.LineID, "Line moved to Client Queries." + (initialProduct?.Supplier ?? "" === "" ? "" : " Last expected from " + (initialProduct?.Supplier ?? "")));
    }
    else if (
      !status.Confirmed
    ) {
      this.orderProcessingService.editLine(selectedLine, null);
      this.noteService.add("OrderLines", selectedLine.LineID, "Line moved to Reviewed with Client");
    }

    this.resetForm();

    if (this.stack && this.current < this.stack.length - 1) {
      this.current += 1;
      this.orderProcessingService.getLine(this.stack[this.current]);
    }
    else {
      this.current += 1;
      this.orderProcessingService.getLine(0);
    }
  }

  public async quoteRequired(): Promise<void> {
    if (!await this.validateLine(false)) {
      return;
    }

    const selectedLine = await firstValueFrom(this.line$);
    if (!selectedLine) return;

    selectedLine.Status = 1011;

    var unpriced: boolean = false;

    if (selectedLine.LineID === 0 || selectedLine.LineID === undefined) {
      const lineId = await this.orderProcessingService.addLine(selectedLine, null);
      if (lineId) {
        this.stack[this.current] = lineId;
      }
    }
    else if (
      selectedLine.Status === 0 ||
      selectedLine.Status === 2011 ||
      selectedLine.Status === 2017 ||
      selectedLine.Status === 2046 ||
      selectedLine.Status === 2047 ||
      selectedLine.Status === 2048
    ) {
      this.orderProcessingService.editLine(selectedLine, null);
    }
    else {
      unpriced = true;
      this.orderLineService.updateStatus(selectedLine.LineID, 2011);
      this.noteService.add("OrderLines", selectedLine.LineID, "Unpriced line marked as needing a quote with Supplier");
    }

    if (!unpriced) {
      this.noteService.add("OrderLines", selectedLine?.LineID ?? 0, "Line moved to Reviewed with Supplier");
    }

    this.resetForm();

    if (this.stack && this.current < this.stack.length - 1) {
      this.current += 1;
      this.orderProcessingService.getLine(this.stack[this.current]);
    }
    else {
      this.current += 1;
      this.orderProcessingService.getLine(0);
    }
  }

  public async addToWebOrder(): Promise<void> {
    if (!await this.validateLine(false)) {
      return;
    }

    const selectedLine = await firstValueFrom(this.line$);
    if (!selectedLine) return;

    selectedLine.Status = 0;
    
    const lineId = await this.orderProcessingService.addLine(selectedLine, null);
    if (lineId) {
      this.stack[this.current] = lineId;
    }

    this.resetForm();

    if (this.stack && this.current < this.stack.length - 1) {
      this.current += 1;
      this.orderProcessingService.getLine(this.stack[this.current]);
    }
    else {
      this.current += 1;
      this.orderProcessingService.getLine(0);
    }
  }
}
